import { useState } from 'react';
import type { RxDocument } from 'rxdb';
import { AnimatePresence, motion, MotionNodeAnimationOptions } from 'motion/react';

import { useTagCreate } from '@/hooks/rxdb-models';
import { useUniqueDocument } from '@/hooks/use-unique-document';

import { Input } from '@flintmint/web-shared/components/input';
import { Button } from '@flintmint/web-shared/components/button';
import { ColorSet } from '@flintmint/web-shared/components/color-set';
import { Popover, type PopoverProps } from '@flintmint/web-shared/components/popover';
import { ColorPicker, type ColorPickerProps } from '@flintmint/web-shared/components/color-picker';

import type { Tag } from '@flintmint/core-shared/rxdb';
import { recommendedColorSet, tagDefaultColor } from '@flintmint/web-shared/constants/ui';

import styles from './new-tag-popover.module.scss';

export const NewTagPopover = (props: NewTagPopoverProps) => {
  const { children, onTagCreate, colorPickerProps, ...rest } = props;

  const [name, setName] = useState('');
  const [open, setOpen] = useState(false);
  const [color, setColor] = useState(tagDefaultColor);

  const { mutate: createTag } = useTagCreate();
  const { isUnique, errorMessage } = useUniqueDocument(name, 'tags');

  const animateViewChange: MotionNodeAnimationOptions = {
    initial: { opacity: 0, y: 8 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -8 },
    transition: { duration: 0.1, ease: 'easeOut' },
  };

  const isValid = isUnique && !!name.trim().length;

  const onClose = () => {
    setName('');
    setOpen(false);
    setColor(tagDefaultColor);
  };

  const handleTagCreate = () => {
    return createTag(
      { name, color },
      {
        onSuccess: (newTag) => {
          onTagCreate?.(newTag as RxDocument<Tag>);
          onClose();
        },
      }
    );
  };

  return (
    <Popover
      open={open}
      onOpen={() => setOpen(true)}
      className={styles.newTagPopover}
      onClose={onClose}
      {...rest}
      content={
        <>
          <Input
            autoFocus
            value={name}
            clearable={false}
            error={!isUnique}
            size="extra-large"
            appearance="underline"
            placeholder="New tag name"
            onKeyDown={(e) => {
              if (isValid && e.key === 'Enter') {
                handleTagCreate();
              }

              if (e.key === 'Escape') {
                onClose();
              }
            }}
            onChange={(e) => setName(e.target.value)}
            left={<ColorPicker color={color} onChange={setColor} {...colorPickerProps} />}
          />
          <div className={styles.newTagPopoverContent}>
            <AnimatePresence mode="wait" initial={false}>
              {isUnique ? (
                <motion.div layout key="unique" className={styles.quickTagColor} {...animateViewChange}>
                  <span className={styles.title}>Quick Tag Color</span>
                  <ColorSet value={color} data={recommendedColorSet} onSelect={(val) => setColor(val)} />
                  <div className={styles.actions}>
                    <Button
                      size="small"
                      color="grey"
                      onClick={onClose}
                      cornerRadius="smooth"
                      appearance="greyscale"
                      data-testid="new-tag-popover-cancel-btn"
                    >
                      Cancel
                    </Button>
                    <Button
                      size="small"
                      color="success"
                      disabled={!isValid}
                      cornerRadius="smooth"
                      onClick={handleTagCreate}
                      data-testid="new-tag-popover-save-btn"
                    >
                      Save
                    </Button>
                  </div>
                </motion.div>
              ) : (
                <motion.div key="error" layout {...animateViewChange}>
                  {errorMessage}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </>
      }
    >
      {children}
    </Popover>
  );
};

interface NewTagPopoverProps extends PopoverProps {
  colorPickerProps?: ColorPickerProps;
  onTagCreate?: (newTag: RxDocument<Tag>) => void;
}
